import { supabase } from '../lib/supabase';
import { orgStore } from '../lib/orgStore';
import { projectStore } from '../lib/projectStore';
import { DrawingElement } from '../types/whiteboard';

export interface SavedWhiteboard {
  id: string;
  user_id: string;
  org_id: string;
  name: string;
  elements: DrawingElement[];
  project_id?: string | null;
  thumbnail?: string;
  created_at: string;
  updated_at: string;
}

function requireOrgId(): string {
  const orgId = orgStore.getOrgId();
  if (!orgId) throw new Error('No organization found');
  return orgId;
}

export const whiteboardService = {
  // RLS scopes to org automatically
  async getWhiteboards(searchQuery?: string, projectId?: string | null): Promise<SavedWhiteboard[]> {
    let query = supabase
      .from('whiteboards')
      .select('*')
      .order('updated_at', { ascending: false });

    if (searchQuery) query = query.ilike('name', `%${searchQuery}%`);
    if (projectId) query = query.eq('project_id', projectId);

    const { data, error } = await query;
    if (error) { console.error('Error fetching whiteboards:', error); throw error; }
    return data || [];
  },

  async getWhiteboard(id: string): Promise<SavedWhiteboard | null> {
    const orgId = requireOrgId();

    const { data, error } = await supabase
      .from('whiteboards')
      .select('*')
      .eq('id', id)
      .eq('org_id', orgId)
      .single();

    if (error) { console.error('Error fetching whiteboard:', error); throw error; }
    return data;
  },

  async createWhiteboard(name: string, elements: DrawingElement[]): Promise<SavedWhiteboard> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const orgId = requireOrgId();

    const { data, error } = await supabase
      .from('whiteboards')
      .insert({ user_id: user.id, org_id: orgId, name, elements, project_id: projectStore.getProjectId() })
      .select()
      .single();

    if (error) { console.error('Error creating whiteboard:', error); throw error; }
    return data;
  },

  // RLS already blocks cross-org writes; the explicit org_id filter is
  // defense in depth so a policy regression cannot turn into a data leak.
  async updateWhiteboard(id: string, updates: { name?: string; elements?: DrawingElement[] }): Promise<SavedWhiteboard> {
    const orgId = requireOrgId();

    const { data, error } = await supabase
      .from('whiteboards')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('org_id', orgId)
      .select()
      .single();

    if (error) { console.error('Error updating whiteboard:', error); throw error; }
    return data;
  },

  async deleteWhiteboard(id: string): Promise<void> {
    const orgId = requireOrgId();

    const { error } = await supabase
      .from('whiteboards')
      .delete()
      .eq('id', id)
      .eq('org_id', orgId);

    if (error) { console.error('Error deleting whiteboard:', error); throw error; }
  },

  async duplicateWhiteboard(id: string, newName: string): Promise<SavedWhiteboard> {
    const original = await this.getWhiteboard(id);
    if (!original) throw new Error('Whiteboard not found');
    return this.createWhiteboard(newName, original.elements);
  },

  async getLibraryItems(): Promise<any[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('user_settings')
      .select('library_items')
      .eq('user_id', user.id)
      .single();

    if (error && error.code !== 'PGRST116') { console.error('Error fetching library items:', error); throw error; }
    return data?.library_items || [];
  },

  async saveLibraryItems(items: any[]): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('user_settings')
      .upsert({ user_id: user.id, library_items: items, updated_at: new Date().toISOString() });

    if (error) { console.error('Error saving library items:', error); throw error; }
  },
};
